import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PortfolioChartBox from './PortfolioChartBox';
import toast from 'react-hot-toast';
import { updateV2PortfolioVisibility, updateAdminPortfolio } from '../../composables/fetchAPI';
import { useMarketPrices } from '../../context/MarketPriceContext';
import { getPortfolioThemeStyle } from '../../utils/portfolioTheme'; // ปรับ Path ตามโครงสร้างโปรเจกต์ของคุณ
// Component ย่อยช่วยให้โค้ดสะอาดขึ้น
const StatCard = ({ label, val, subVal, color }: any) => (
  <div className={`bg-[var(--component-bg-color)] p-4 rounded-xl border-2 border-${color}-500/50 shadow-[0_0_10px_rgba(234,179,8,0.5)] flex flex-col justify-between min-w-[200px]`}>
    <div className="text-xs font-bold text-gray-400" style={{ color: 'var(--text-color)' }}>{label}</div>
    <div className="text-base font-bold text-white" style={{ color: 'var(--text-color)' }}>${val.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
    {subVal !== undefined && <div className="text-xs text-gray-500" style={{ color: 'var(--text-color)' }}>ต้นทุน: ${subVal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>}
  </div>
);

const StatCardProfit = ({ label, val, percent, color }: any) => {
  // 1. เช็คว่ากำไรเป็นบวกหรือไม่
  const isProfit = val >= 0;
  // 2. กำหนดคลาสสีตามสภาวะกำไร/ขาดทุน
  const textClass = isProfit ? 'text-green-500' : 'text-red-500';

  return (
    <div className={`bg-[var(--component-bg-color)] p-4 rounded-xl border-2 ${isProfit ? 'border-green-500/50' : 'border-red-500/50'} shadow-[0_0_10px_rgba(234,179,8,0.5)]  flex flex-col justify-between min-w-[200px]`}>
      <div className="text-xs font-bold text-gray-400" style={{ color: 'var(--text-color)' }}>{label}</div>
      <div className={`text-base font-bold ${textClass}`}>
        {val > 0 ? '+' : ''}${val.toLocaleString(undefined, { minimumFractionDigits: 2 })}
      </div>
      {/* 3. นำ textClass มาใช้ที่นี่ด้วย เพื่อให้เปอร์เซ็นต์เปลี่ยนสีตาม */}
      <div className={`text-[11px] font-bold ${textClass}`}>
        ({percent >= 0 ? '+' : ''}{percent.toFixed(2)}%)
      </div>
    </div>
  );
};
const PortfolioAllocationBoxV2Admin = ({
  portfolios, // 🎯 ดึงจาก API เส้น Public ล้วนๆ ไม่มีการยิงซ้ำ
  onAddPortfolio,
  setPortfolios,
  onEditPortfolio,
  onDeletePortfolio,
  isAdmin,
  refreshPortfolios
}: any) => {
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const { currentPrices, fetchPricesForSymbols, isLoadingPrices } = useMarketPrices();
  // 1. คำนวณ Stats แบบเดียวกับ V2
  const stats = useMemo(() => {
    let totalStockMarketValue = 0;
    let totalCostValue = 0;
    let totalCash = 0;
    let totalRealizedGain = 0;

    portfolios.forEach((portfolio: any) => {
      if (portfolio.sourceType === 'ADMIN_PORTFOLIO') return;
      totalCash += (portfolio.summary?.cashValue ?? portfolio.cashBalances?.[0]?.amount ?? 0);
      totalRealizedGain += (portfolio.summary?.realizedGain ?? 0);

      const activeSubPorts = portfolio.subPorts || [];
      activeSubPorts.forEach((subPort: any) => {
        const items = subPort.holdings || subPort.stocks || [];
        items.forEach((item: any) => {
          const symbol = item.stockSymbol || item.assetSymbol || item.symbol;
          const price = currentPrices[symbol] || 0;
          const costVal = item.costValue || ((item.quantity || 0) * (item.avgEntryPrice || item.avgBuyPrice || 0));
          totalStockMarketValue += (item.quantity || 0) * price;
          totalCostValue += costVal;
        });
      });
    });

    const unrealizedGain = totalStockMarketValue - totalCostValue;
    const totalProfitCombined = totalRealizedGain + unrealizedGain;

    return {
      stockValue: totalStockMarketValue,
      costValue: totalCostValue,
      cashValue: totalCash,
      profit: unrealizedGain, // กำไรทิพย์
      profitPercent: totalCostValue > 0 ? (unrealizedGain / totalCostValue) * 100 : 0,
      realizedGain: totalRealizedGain,
      realizedGainPercent: totalCostValue > 0 ? (totalRealizedGain / totalCostValue) * 100 : 0,
      totalValue: totalStockMarketValue + totalCash,
      totalProfitCombined,
      totalProfitPercentCombined: totalCostValue > 0 ? (totalProfitCombined / totalCostValue) * 100 : 0
    };
  }, [portfolios, currentPrices]);
  useEffect(() => {
    const checkSize = () => setIsMobile(window.innerWidth < 768);
    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);

  const handleToggleVisibility = async (portfolio: any) => {
    const confirm = window.confirm('คุณต้องการนำพอร์ตนี้ออกจากพอร์ตแอดมินจริงๆ ใช่หรือไม่?');
    if (!confirm) return;

    if (isUpdating) return;
    setIsUpdating(true);
    try {
      const portId = portfolio.portfolioId || portfolio.portId || portfolio.id || portfolio.adminPortfolioId;

      if (portfolio.slug) {
        // ถ้าเป็นพอร์ตแอดมินแบบใหม่ที่มี slug สามารถใช้วิธีอัปเดตสถานะเป็น private หรือใช้ archive ตามที่ระบบรองรับ
        await updateAdminPortfolio(portId, { visibilityStatus: 'private' });
      } else {
        await updateV2PortfolioVisibility(portId, 'private');
      }

      toast.success(`นำออกจากพอร์ตแอดมินสำเร็จ`);
      await refreshPortfolios();
    } catch (error) {
      console.error('Error updating visibility:', error);
      toast.error('ไม่สามารถอัปเดตข้อมูลได้');
    } finally {
      setIsUpdating(false);
    }
  };

  const calculateTotalValueAndProfit = () => {
    let totalValue = 0;
    let totalCost = 0;

    portfolios.forEach((portfolio: any) => {
      if (portfolio.sourceType === 'ADMIN_PORTFOLIO') return;
      portfolio.subPorts?.forEach((subPort: any) => {
        const items = subPort.holdings || subPort.stocks || [];

        // 🎯 ถ้า Backend ส่งหุ้นมาให้ในอนาคต มันจะเข้าเงื่อนไขนี้และโชว์ Real-time ทันที
        if (items.length > 0) {
          items.forEach((item: any) => {
            const symbol = item.assetSymbol || item.stockSymbol;
            const avgPrice = item.avgEntryPrice || item.avgBuyPrice || 0;
            const quantity = item.quantity || 0;
            const price = currentPrices[symbol] || 0;

            totalValue += quantity * price;
            totalCost += quantity * avgPrice;
          });
        } else {
          // 🚨 ตอนนี้ API ส่ง "holdings": [] มา มันเลยต้องดึง Cost จาก Summary เพื่อไม่ให้กราฟแหว่ง
          totalValue += subPort.summary?.assetCostValue || 0;
          totalCost += subPort.summary?.assetCostValue || 0;
        }
      });

      const cashValue = portfolio.summary?.cashValue || portfolio.cashBalances?.[0]?.amount || 0;
      totalValue += cashValue;
      totalCost += cashValue;
    });

    const overallProfit = totalValue - totalCost;
    const overallProfitPercent = totalCost > 0 ? (overallProfit / totalCost) * 100 : 0;

    return { totalValue, overallProfit, overallProfitPercent };
  };

  const calculateTotalRatio = () => {
    return portfolios.reduce((sum: number, portfolio: any) => {
      // 🚫 ข้าม ADMIN_PORTFOLIO
      if (portfolio.sourceType === 'ADMIN_PORTFOLIO') return sum;
      return sum + (portfolio.expectedRatio || 0);
    }, 0);
  };
  const totalRatio = calculateTotalRatio();

  // รวบรวมชื่อหุ้นเพื่อโหลดราคา (จะทำงานก็ต่อเมื่อ Backend ยอมส่งรายชื่อหุ้นมา)
  useEffect(() => {
    if (!portfolios || portfolios.length === 0) return;

    const uniqueSymbols = new Set<string>();
    portfolios.forEach((p: any) => {
      if (p.subPorts) {
        p.subPorts.forEach((sub: any) => {
          const items = sub.holdings || sub.stocks || [];
          items.forEach((item: any) => uniqueSymbols.add(item.assetSymbol || item.stockSymbol));
        });
      }
    });

    const symbolArray = Array.from(uniqueSymbols).filter(Boolean);
    if (symbolArray.length > 0) {
      fetchPricesForSymbols(symbolArray);
    }
  }, [portfolios, fetchPricesForSymbols]);

  const { totalValue, overallProfit, overallProfitPercent } = calculateTotalValueAndProfit();

  return (
    <div className={`w-full ${isMobile ? 'px-4' : 'px-16'}`}>
      <h1 className='text-yellow-500' style={{ color: 'var(--yellow-text-color)' }}> (พอร์ตช่อง เท่านั้น)</h1>

      <div className="flex md:grid md:grid-cols-5 gap-4 w-full overflow-x-auto py-2 px-2 mb-6">
        <StatCard label="มูลค่าหุ้น / ต้นทุน" val={stats.stockValue} subVal={stats.costValue} color="yellow" />
        <StatCardProfit label="กำไร/ขาดทุน ทิพย์" val={stats.profit} percent={stats.profitPercent} color="green" />
        <StatCardProfit label="กำไร ล็อคแล้ว" val={stats.realizedGain} percent={stats.realizedGainPercent} color="green" />
        <StatCard label="เงินสด" val={stats.cashValue} color="blue" />
        <StatCardProfit label="มูลค่าพอร์ตรวม" val={stats.totalValue} percent={stats.totalProfitPercentCombined} color="purple" />
      </div>

      <div className="flex flex-wrap gap-8 justify-center">
        {portfolios.map((p: any) => {
          const isShayPort = Boolean(p.slug) || p.name?.toLowerCase() === 'shay';
          // 🎯 เพิ่มเงื่อนไขเช็คว่าชื่อพอร์ตมีคำว่า "growth" หรือไม่ (ไม่สนตัวพิมพ์เล็ก-ใหญ่)
          const portNameLower = (p.name || '').toLowerCase();
          const isGrowthPort = portNameLower.includes('พอร์ตรอง');
          const themeStyle = getPortfolioThemeStyle(p.settings?.themeConfig);
          // 🎨 กำหนดคลาส CSS กรอบเรืองแสง: ถ้าเป็น Growth ให้ใช้สไตล์ไล่สี ส้ม-เหลือง-แดง
          const glowContainerClass = isGrowthPort
            ? "glow-port-growth-container cursor-pointer transition-transform hover:scale-[1.02]"
            : "glow-port-container cursor-pointer transition-transform hover:scale-[1.02]";
          const cardContent = (
            <>
              <div className='flex justify-between items-center mb-2'>
                <div
                  className="text-xl font-bold flex-1"
                  style={{ color: 'var(--text-color)', textAlign: 'start', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {p.name} <span className='text-yellow-400 text-lg'>{p.expectedRatio ? `(${p.expectedRatio}%)` : ''}</span>
                </div>
                {isAdmin && (
                  <div
                    className="bg-red-500/80 hover:bg-red-500 text-white rounded shadow px-3 py-1.5 cursor-pointer ml-2 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleVisibility(p);
                    }}
                  >
                    <span className="text-[11px] md:text-xs font-bold">นำออก</span>
                  </div>
                )}
              </div>

              <PortfolioChartBox
                portfolios={p.subPorts || []}
                currentPrices={currentPrices}
                useSubPorts={true}
                fromAdmin={true}
                loading={false}
                cashAmount={p.summary?.cashValue || p.cashBalances?.[0]?.amount || 0}
                layout='vertical'
              />
            </>
          );

          return (
            <div key={p.id || p.portfolioId}>
              {isShayPort ? (
                <div
                  onClick={() => {
                    const portId = p.portfolioId || p.portId || p.id || p.adminPortfolioId;
                    if (p.slug) {
                      // ✅ เพิ่ม state: { fromAdmin: true, portfolioData: p } ตรงนี้ด้วย
                      navigate(`/admin-Shay/${portId}`, {
                        state: { fromAdmin: true, portfolioData: p }
                      });
                    } else {
                      navigate(`/admin/${portId}`, { state: { portfolioData: p } });
                    }
                  }}
                  className={glowContainerClass}
                  style={{ minWidth: 380, maxWidth: 380, ...themeStyle }}
                >
                  <div className="p-4 flex flex-col justify-center bg-[var(--component-bg-color)] rounded-[calc(0.9em-3px)]">
                    {cardContent}
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => {
                    const portId = p.portfolioId || p.portId || p.id || p.adminPortfolioId;
                    if (p.slug) {
                      navigate(`/admin-Shay/${portId}`, {
                        state: { fromAdmin: true, portfolioData: p }
                      });
                    } else {
                      navigate(`/admin/${portId}`, { state: { portfolioData: p } });
                    }
                  }}
                  // ✅ คืนค่า className เดิมที่ถูกต้อง (กรอบเหลืองเงาเรืองแสง) กลับมาตรงนี้
                  className="rounded-xl shadow-[0_0_15px_rgba(234,179,8,0.3)] transition-all duration-300 hover:shadow-[0_0_25px_rgba(234,179,8,0.5)] border-2 border-yellow-500/50 p-4 cursor-pointer flex flex-col justify-center hover:scale-[1.02]"
                  style={{ minWidth: 380, maxWidth: 380, ...themeStyle }}
                >
                  {cardContent}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PortfolioAllocationBoxV2Admin;